import routes from 'js/core_pre_webpack/routes';

// The one file under profiles/autotest allowed to import the generated routes
// helper, because the helper escapes nothing. relativeRoute in
// core_pre_webpack/routes.js pushes path params straight into the segment list
// and builds the query as `${key}=${value}`, so &, ?, #, a space or any
// non-ASCII character lands in the URL verbatim - in the path as much as in the
// query. Every string below is encoded on the way in, exactly once, so an arm
// gets a finished URL and never has to know any of this.
//
// Two further traps in that helper, both handled by only ever passing it a
// fresh object literal with keys that are actually set: it DELETES the keys it
// consumes off the object it was handed (and writes subdomain_param back onto
// it), and an undefined value survives interpolation as the literal text
// "undefined" rather than dropping out.

export function workEditUrl(workId: number): string {
  return routes.work_edit_url({ id: workId });
}

export function uploadDocumentUrl(source: string): string {
  return routes.upload_funnel_document_url({
    source: encodeURIComponent(source),
  });
}

export function profileEditUrl(pageName: string): string {
  return routes.edit_new_profile_url({
    page_name: encodeURIComponent(pageName),
  });
}

// Rails routes the dashboard as ":page_name/Analytics(/*react_path)" - one
// route with a glob, so the sub-views are client routes with no helper of their
// own and the helper only ever yields the base. Concatenating the view is what
// Navbar and the cancel-premium cards do too.
const ANALYTICS_VIEWS = {
  readers: '/activity/readers',
  countries: '/activity/countries',
} as const;

export type AnalyticsView = keyof typeof ANALYTICS_VIEWS;

// subdomain is the one argument here that is neither a path segment nor a query
// value: absoluteRoute splices it into the host, where percent-encoding buys
// nothing and a '/' would move the link to another origin entirely. Anything
// that is not a plain host label is dropped, which leaves the helper on its own
// default of www.
const HOST_LABEL = /^[a-z\d][a-z\d.-]*$/i;

export function analyticsDashboardUrl(
  pageName: string,
  view: AnalyticsView,
  subdomain?: string,
): string {
  const base = routes.analytics_dashboard_url({
    page_name: encodeURIComponent(pageName),
    ...(subdomain != null && HOST_LABEL.test(subdomain) ? { subdomain } : {}),
  });

  return `${base}${ANALYTICS_VIEWS[view]}`;
}

// The four API destinations are relative paths, fetched same-origin. That only
// reaches an api-constrained route because SubdomainParamMiddleware reads
// subdomain_param off the query and rewrites the request host before routing;
// drop it and the request 404s. The helper would add it itself for these
// routes, but passing it fixes where it sits in the query string.
const API = 'api';

export function researchInterestWorksPath(
  interestId: number,
  pageSize: number,
): string {
  return routes.works_api_v0_research_interest_path({
    subdomain_param: API,
    id: interestId,
    page_size: pageSize,
  });
}

export function hitsByCountryPath(userId: number): string {
  return routes.all_time_hits_by_country_api_v0_user_stats_path({
    subdomain_param: API,
    user_id: userId,
  });
}

export function universityHitCountsPath(userId: number): string {
  return routes.university_hit_counts_api_v0_user_stats_path({
    subdomain_param: API,
    user_id: userId,
  });
}

// This route has no path components of its own, so all four values land in the
// query - page 0 included, which survives because the helper builds the query
// off Object.entries rather than off truthiness. Which page and how many per
// page is pageData's policy, not this file's.
export function profileWorksJsonPath(
  userId: number,
  page: number,
  perPage: number,
): string {
  return routes.api_v0_profiles_works_json_index_path({
    subdomain_param: API,
    user_id: userId,
    page,
    per_page: perPage,
  });
}

export function searchPath(query: string): string {
  return routes.search_path({ q: encodeURIComponent(query) });
}
